import { useEffect, useRef, useState } from 'react';
import { getText } from '@/translate/text';
import { Mail, Users, UserCheck, Search, Trash, Check, CheckCheck } from 'lucide-react';
import { patientService } from '@/services/patientService';
import UsersList from '@/components/notificationCenter/UsersList';
import { NotificationService } from '@/services/notificationService';
export default function WhitelistManagement({ isOpen, onClose, onSendTest }) {
  const [activeTab, setActiveTab] = useState('whitelist');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newWhitelistCount, setNewWhitelistCount] = useState(0);
  const searchScrollPosition = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const pagingLockRef = useRef(false);
  const isSelected = (id) => selectedUsers.includes(id);

  const toggleUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
    );
  };

  const allSelected =
    whitelist.length > 0 && whitelist.every((u) => selectedUsers.includes(u.user_id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(whitelist.map((u) => u.user_id));
    }
  };

  const handleSearchScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isScrollingDown = scrollTop > searchScrollPosition.current;
    searchScrollPosition.current = scrollTop;

    const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;
    if (!isScrollingDown || !nearBottom || pagingLockRef.current) return;
    if (totalPages !== null && page >= totalPages) return;

    // Lock immediately to prevent multiple increments
    pagingLockRef.current = true;
    setPage((prev) => prev + 1);
  };

  // 🔒 Prevent background scroll while the popup is open
  useEffect(() => {
    if (!isOpen) return;
    fetchUsers();
    getWhitelist();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    setActiveTab('whitelist');
    setSearchResults([]);
    setSearch('');
    setPage(1);
    setLimit(10);
    setSelectedUsers([]);
    console.log('onClose');
  }, [onClose]);

  useEffect(() => {
    getWhitelist();
  }, [activeTab]);

  // 🔎 Reset paging when search term changes
  useEffect(() => {
    setPage(1);
    setTotalPages(null);
    setSearchResults([]);
    pagingLockRef.current = false;
  }, [search]);

  // 🔎 Fetch users when search or page changes
  useEffect(() => {
    fetchUsers();
  }, [search, page]);

  const fetchUsers = async () => {
    if (totalPages !== null && page > totalPages) {
      pagingLockRef.current = false;
      return;
    }

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);

    fetchTimeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await patientService.getPatients(page, limit, search, {});
        setTotalPages(response?.totalPages ?? 1);
        const data = response?.data || [];
        setSearchResults((prev) => (page === 1 ? data : [...prev, ...data]));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        pagingLockRef.current = false;
      }
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, []);

  const addUsersToWhitelist = async (user_id) => {
    try {
      const response = await NotificationService.addUsersToWhitelist(user_id);
      getWhitelist();
      if (activeTab !== 'whitelist') {
        setNewWhitelistCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const removeUsersFromWhitelist = async (user_id) => {
    try {
      const response = await NotificationService.removeUsersFromWhitelist(user_id);
      getWhitelist();
    } catch (err) {
      console.error(err);
    }
  };

  const getWhitelist = async () => {
    try {
      const response = await NotificationService.getWhitelist();
      setWhitelist(response.body.allowlist);
    } catch (err) {
      console.error(err);
    }
  };

  const emptyState = (title, description) => {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-center">
          <UserCheck className="w-10 h-10 text-gray-300" />
          <p className="text-md text-gray-400">{title || 'No users exists'}</p>
          <p className="text-sm text-gray-400">
            {description || 'Try again with different search'}
          </p>
        </div>
      </div>
    );
  };

  const isAdded = (id) => {
    if (!id) return false;
    return whitelist?.some((u) => String(u?.user_id) === String(id)) ?? false;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-full h-dvh"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-[520px] min-h-[300px] max-h-[90dvh] rounded-2xl shadow-xl p-4 flex flex-col">
        <div className="flex justify-start items-center">
          <Mail className="w-5 h-5 me-1 text-primary-600" />
          <h2 className="text-lg font-semibold">
            {getText('whitelist_management') || 'Whitelist Management'}
          </h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {getText('choose_who_will_receive_the_test_notification') ||
            'Choose who will receive the test notification'}
          {getText('choose_who_will_receive_the_test_notification') ||
            'Choose who will receive the test notification'}
        </p>
        {/* Tabs */}
        <div className="flex justify-center border-b mb-4 gap-5">
          <div
            className={`${activeTab === 'whitelist' ? 'border-b-2 border-primary-600 text-primary-600' : 'text-gray-500'} w-50 flex justify-start items-center`}
          >
            <button
              className={`flex justify-center items-center py-2 text-sm font-medium mx-auto`}
              onClick={() => {
                setActiveTab('whitelist');
                setNewWhitelistCount(0);
              }}
            >
              <Users className="w-5 h-5 me-1" />
              {getText('whitelist') || 'Whitelist'}
              {newWhitelistCount > 0 && (
                <span className="bg-primary-100 w-5 h-5 flex items-center justify-center ms-2 rounded-full text-primary-600 text-xs text-center">
                  {newWhitelistCount}
                </span>
              )}
            </button>
          </div>
          <div
            className={`w-50 ${activeTab === 'search' ? 'border-b-2 border-primary-600 text-primary-600' : 'text-gray-500'} flex justify-start items-center`}
          >
            <button
              className={`flex justify-center items-center py-2 text-sm font-medium mx-auto`}
              onClick={() => setActiveTab('search')}
            >
              <Search className="w-5 h-5 me-1" />
              Search Users
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* SEARCH TAB */}
          {activeTab === 'search' && (
            <>
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-0 "
                />
              </div>

              <div className="flex-1 overflow-y-auto space-y-2 pr-1" onScroll={handleSearchScroll}>
                {searchResults.length === 0 &&
                  emptyState(
                    getText('no_users_exists'),
                    getText('try_again_with_different_search')
                  )}
                {loading && <p className="text-sm">{getText('searching') || 'Searching'}...</p>}

                <UsersList
                  users={searchResults}
                  hasBorder={true}
                  onAdd={(user_id) => addUsersToWhitelist(user_id)}
                  isAdded={isAdded}
                />
              </div>
            </>
          )}

          {activeTab === 'whitelist' && (
            <div className="flex-1 flex flex-col min-h-0">
              {/* Top Bar */}
              {whitelist.length > 0 && (
                <div className="flex items-center justify-between mb-2 px-1">
                  {/* Select All */}
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 text-sm text-gray-600"
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-1 flex items-center justify-center transition 
              ${allSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}
            `}
                    >
                      {allSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    {allSelected ? getText('deselect_all') : getText('select_all')}
                  </button>

                  {/* Selected Count */}
                  {selectedUsers.length > 0 && (
                    <div className="flex items-center gap-1 text-primary-600 text-sm font-medium">
                      <CheckCheck className="w-4 h-4" />
                      {selectedUsers.length} selected
                    </div>
                  )}
                </div>
              )}

              {/* Empty State */}
              {whitelist.length === 0 &&
                emptyState(
                  getText('no_users_in_whitelist'),
                  getText('switch_to_search_users_to_add_users')
                )}

              {/* List */}
              {whitelist.length > 0 && (
                <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                  {whitelist.map((user) => {
                    const id = user.user_id;
                    const selected = isSelected(id);

                    return (
                      <div
                        key={id}
                        className={`flex items-center justify-between border rounded-xl p-3 transition
                ${selected ? 'border-primary-500' : 'border-gray-200'}
              `}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          <button
                            onClick={() => toggleUser(id)}
                            className={`w-5 h-5 rounded-md border-1 flex items-center justify-center transition
                    ${selected ? 'border-primary-600 bg-primary-600' : 'border-gray-300 bg-white'}
                  `}
                          >
                            {selected && <Check className="w-4 h-4 text-white" />}
                          </button>

                          {/* User Info */}
                          <div>
                            <p className="font-medium text-sm">
                              {user.user_info.first_name} {user.user_info.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{user.user_info.mobile_number}</p>
                          </div>
                        </div>

                        {/* Trash */}
                        <button onClick={() => removeUsersFromWhitelist(user.user_id)}>
                          <Trash className="w-5 h-5 text-gray-400 hover:text-red-500 transition" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        {/* line */}
        <div className="flex justify-start mt-4">
          <div className="w-full h-[1px] bg-gray-200 dark:bg-gray-700"></div>
        </div>
        {/* Footer - always sticks to bottom */}
        <div className="flex justify-center gap-2 mt-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg w-50">
            Cancel
          </button>

          <button
            disabled={selectedUsers.length === 0}
            onClick={() => onSendTest(selectedUsers)}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 w-50"
          >
            {getText('send_test')}{' '}
            {`${selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
